import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { transactionService } from "../services/transactionService";
import FilterSection from "../components/TransactionPage/FilterSection";
import ChartSection from "../components/TransactionPage/ChartSection";
import SummaryCard from "../components/TransactionPage/SummaryCard";
import TopSpending from "../components/TransactionPage/TopSpending";
import Header from "../components/TransactionPage/Header";
import ExpensePieChart from "../components/TransactionPage/ExpensePieChart"; // Import the ExpensePieChart component
import { Tooltip } from 'bootstrap';
import Spinner from "../components/Spinner";

const TransactionPage = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [monthlyData, setMonthlyData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [incomeTransactions, setIncomeTransactions] = useState([]);
  const [expenseTransactions, setExpenseTransactions] = useState([]);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpense, setTotalExpense] = useState(0);
  const [categorizedExpenses, setCategorizedExpenses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("idToken");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchTransactions();
  }, [navigate]);

  // Add useEffect to handle month changes
  useEffect(() => {
    if (transactions.length > 0) {
      updateDataForMonth(selectedMonth);
    }
  }, [selectedMonth, transactions]); // Add transactions as dependency

  const getMonthRange = (month) => {
    return month < 6 ? monthlyData.slice(0, 6) : monthlyData.slice(6);
  };

  const updateDataForMonth = (month) => {
    // Filter transactions for selected month
    const filteredTransactions = transactions.filter(transaction => {
      const transactionDate = new Date(transaction.date);
      return transactionDate.getMonth() === month;
    });

    console.log('Filtered transactions for month:', month, filteredTransactions); // Debug log

    // Update income and expense transactions
    const income = filteredTransactions.filter(t => t.type === "income");
    const expenses = filteredTransactions.filter(t => t.type === "expense");
    
    setIncomeTransactions(income);
    setExpenseTransactions(expenses);

    // Update totals
    const totalInc = income.reduce((sum, t) => sum + t.amount, 0);
    const totalExp = expenses.reduce((sum, t) => sum + t.amount, 0);
    
    setTotalIncome(totalInc);
    setTotalExpense(totalExp);

    // Update categorized expenses
    const expenseCategories = categorizeExpensesByCategory(expenses);
    setCategorizedExpenses(expenseCategories);

    console.log('Updated categorized expenses:', expenseCategories); // Debug log
  };

  const categorizeExpensesByCategory = (expenses) => {
    const categories = {};
    expenses.forEach((expense) => {
      const category = (expense.category || "Others").toLowerCase();
      if (!categories[category]) {
        categories[category] = 0;
      }
      categories[category] += expense.amount;
    });
    return categories;
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await transactionService.getAllTransactions();
      console.log('Fetched transactions:', data); // Debug log
      
      setTransactions(data);
      
      // Group data by month
      const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];

      const monthlySummary = months.map((month, index) => {
        const monthTransactions = data.filter(t => {
          const transactionDate = new Date(t.date);
          return transactionDate.getMonth() === index;
        });
        
        const monthIncome = monthTransactions
          .filter(t => t.type === "income")
          .reduce((sum, t) => sum + t.amount, 0);
        
        const monthExpense = monthTransactions
          .filter(t => t.type === "expense")
          .reduce((sum, t) => sum + t.amount, 0);

        return {
          month,
          income: monthIncome || 0,
          expense: monthExpense || 0,
        };
      });

      setMonthlyData(monthlySummary);
      
      // Initialize with current month's data
      updateDataForMonth(selectedMonth);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setError("Failed to load transactions.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 custom-emerald pb-5">
      <Header title="Transactions" />
      <div className="p-4 text-white">
        <FilterSection
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
        />
        <ChartSection
          monthlyData={getMonthRange(selectedMonth)}
          selectedMonth={selectedMonth}
        />
      </div>
      <div className="bg-white p-4">
        {loading ? (
          <Spinner />
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <>
            <SummaryCard
              totalIncome={totalIncome}
              totalExpense={totalExpense}
              selectedMonth={selectedMonth}
              transactions={transactions}
            />
            {Object.keys(categorizedExpenses).length > 0 ? (
              <>
                <ExpensePieChart 
                  categories={Object.keys(categorizedExpenses)} 
                  amounts={Object.values(categorizedExpenses)} 
                />
                <TopSpending categorizedExpenses={categorizedExpenses} />
              </>
            ) : (
              <div className="text-center py-4">
                No expenses found for {new Date(2024, selectedMonth).toLocaleString('default', { month: 'long' })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionPage;
